import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, UserPlus, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CourseEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName: string;
  onUpdate: () => void;
}

interface Enrollment {
  id: string;
  contact_id: string;
  enrollment_status: string;
  payment_status: string;
  notes: string;
  created_at: string;
  contact: {
    id: string;
    name: string;
    email: string;
    phone: string;
    gender: string;
  };
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  gender: string;
}

const CourseEnrollmentModal: React.FC<CourseEnrollmentModalProps> = ({
  isOpen, onClose, courseId, courseName, onUpdate
}) => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (isOpen && courseId) {
      loadEnrollments();
    }
  }, [isOpen, courseId]);

  const loadEnrollments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('*, contact:contacts(id, name, email, phone, gender)')
        .eq('course_id', courseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEnrollments((data || []) as unknown as Enrollment[]);
    } catch (error) {
      console.error('Error loading enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchContacts = async (term: string) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, name, email, phone, gender')
        .or(`name.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%`)
        .limit(10);

      if (error) throw error;

      const enrolledIds = new Set(enrollments.map(e => e.contact_id));
      setSearchResults((data || []).filter(c => !enrolledIds.has(c.id)));
    } catch (error) {
      console.error('Error searching contacts:', error);
    } finally {
      setSearching(false);
    }
  };

  const addEnrollment = async (contact: Contact) => {
    try {
      const { error } = await supabase.from('course_enrollments').insert({
        course_id: courseId,
        contact_id: contact.id,
      });

      if (error) throw error;

      await supabase
        .from('courses')
        .update({ participant_count: enrollments.length + 1 })
        .eq('id', courseId);

      setSearchTerm('');
      setSearchResults([]);
      loadEnrollments();
      onUpdate();
    } catch (error) {
      console.error('Error adding enrollment:', error);
    }
  };

  const removeEnrollment = async (enrollmentId: string) => {
    if (!confirm('Rimuovere questo iscritto dal corso?')) return;

    try {
      const { error } = await supabase
        .from('course_enrollments')
        .delete()
        .eq('id', enrollmentId);

      if (error) throw error;

      await supabase
        .from('courses')
        .update({ participant_count: Math.max(0, enrollments.length - 1) })
        .eq('id', courseId);

      loadEnrollments();
      onUpdate();
    } catch (error) {
      console.error('Error removing enrollment:', error);
    }
  };

  const updateEnrollmentStatus = async (enrollmentId: string, field: string, value: string) => {
    try {
      const { error } = await supabase
        .from('course_enrollments')
        .update({ [field]: value })
        .eq('id', enrollmentId);

      if (error) throw error;
      loadEnrollments();
    } catch (error) {
      console.error('Error updating enrollment:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 p-5 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">Iscritti al Corso</h2>
            <p className="text-white/80 text-sm">{courseName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Aggiungi Partecipante</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca per nome, email o telefono..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => searchContacts(e.target.value)}
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 animate-spin" />
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                {searchResults.map(contact => (
                  <button
                    key={contact.id}
                    onClick={() => addEnrollment(contact)}
                    className="w-full text-left px-4 py-3 hover:bg-rose-50 flex items-center justify-between border-b border-gray-100 last:border-0 transition-colors"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{contact.name}</div>
                      <div className="text-sm text-gray-500">{contact.email} - {contact.phone}</div>
                    </div>
                    <UserPlus className="h-4 w-4 text-rose-500" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
            </div>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nessun iscritto per questo corso
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-700">Nome</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-700">Contatto</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-700">Stato</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-700">Pagamento</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-700"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {enrollments.map(enrollment => (
                    <tr key={enrollment.id} className="hover:bg-gray-50">
                      <td className="py-2.5 px-3 font-medium text-gray-900">
                        {enrollment.contact?.name}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600">
                        <div>{enrollment.contact?.email}</div>
                        <div className="text-xs">{enrollment.contact?.phone}</div>
                      </td>
                      <td className="py-2.5 px-3">
                        <select
                          value={enrollment.enrollment_status}
                          onChange={(e) => updateEnrollmentStatus(enrollment.id, 'enrollment_status', e.target.value)}
                          className="text-xs rounded border-gray-300 focus:ring-rose-500 focus:border-rose-500"
                        >
                          <option value="iscritto">Iscritto</option>
                          <option value="confermato">Confermato</option>
                          <option value="annullato">Annullato</option>
                          <option value="completato">Completato</option>
                        </select>
                      </td>
                      <td className="py-2.5 px-3">
                        <select
                          value={enrollment.payment_status}
                          onChange={(e) => updateEnrollmentStatus(enrollment.id, 'payment_status', e.target.value)}
                          className="text-xs rounded border-gray-300 focus:ring-rose-500 focus:border-rose-500"
                        >
                          <option value="da pagare">Da Pagare</option>
                          <option value="pagato">Pagato</option>
                          <option value="rimborsato">Rimborsato</option>
                        </select>
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => removeEnrollment(enrollment.id)}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                          title="Rimuovi"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollmentModal;
